import React, { useState } from "react";
import { Empty, Button, Space, Tooltip, Typography } from "antd";
import {
  FullscreenOutlined,
  DownloadOutlined,
  CopyOutlined,
} from "@ant-design/icons";

import { downloadMedia, copyToClipboard } from "../utils";
import "./MediaGallery.css";
import "./MediaGalleryCarousel.css";

const MediaGallery = ({ inputs, outputs, prompts, onImageClick }) => {
  const [hoveredImage, setHoveredImage] = useState(null);
  const [currentOutputIndex, setCurrentOutputIndex] = useState(0);
  const [touchStart, setTouchStart] = useState({ x: 0 });
  const [touchMove, setTouchMove] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Swipe handlers for output images carousel
  const handleTouchStart = (e) => {
    if (outputs.length <= 1) return;
    setTouchStart({ x: e.touches[0].clientX });
    setTouchMove(0);
    setIsDragging(true);
  };
  
  const handleTouchMove = (e) => {
    if (!isDragging || outputs.length <= 1) return;
    
    const currentX = e.touches[0].clientX;
    const diffX = currentX - touchStart.x;
    
    e.preventDefault();
    
    // Apply movement with resistance at boundaries
    let adjustedMove = diffX;
    if (currentOutputIndex === 0 && diffX > 0) {
      adjustedMove = diffX * 0.3;
    } else if (currentOutputIndex === outputs.length - 1 && diffX < 0) {
      adjustedMove = diffX * 0.3;
    }
    
    setTouchMove(adjustedMove);
  };
  
  const handleTouchEnd = (e) => {
    if (!isDragging || outputs.length <= 1) return;
    
    const threshold = 60;
    
    if (Math.abs(touchMove) > threshold) {
      if (touchMove > 0 && currentOutputIndex > 0) {
        setCurrentOutputIndex(prev => prev - 1);
      } else if (touchMove < 0 && currentOutputIndex < outputs.length - 1) {
        setCurrentOutputIndex(prev => prev + 1);
      }
    }
    
    setTouchMove(0);
    setIsDragging(false);
  };

  const handleImageSelect = (image) => {
    onImageClick(image);
  };

  const handleDownload = async (image) => {
    await downloadMedia([image]);
  };

  const handleCopyUrl = (url) => {
    copyToClipboard(url);
  };

  const renderImageCard = (image, isInput = false) => {
    const isVideo = image.type === "video";
    // Reference-audio inputs reach this gallery now that job history detects
    // audio URLs; without this branch they fell through to <img>.
    const isAudio = image.type === "audio";

    return (
      <div
        key={image.id}
        className="media-card"
        onMouseEnter={() => setHoveredImage(image.id)}
        onMouseLeave={() => setHoveredImage(null)}
        onClick={() => handleImageSelect(image)}
      >
        <div className="media-preview">
          {isVideo ? (
            <video
              src={image.url}
              className="media-content"
              muted
              loop
              playsInline
              onMouseEnter={(e) => e.target.play()}
              onMouseLeave={(e) => e.target.pause()}
            />
          ) : isAudio ? (
            <div className="media-content" style={{ display: 'grid', placeItems: 'center', gap: 8, padding: 12 }}>
              <span style={{ fontSize: 28, opacity: 0.7 }}>♪</span>
              <audio
                src={image.url}
                controls
                preload="metadata"
                style={{ width: '100%' }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ) : (
            <img src={image.url} alt={image.label} className="media-content" />
          )}

          {hoveredImage === image.id && (
            <div className="media-overlay">
              <Space direction="vertical" size={8}>
                <Button
                  type="primary"
                  icon={<FullscreenOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onImageClick(image);
                  }}
                  className="overlay-btn"
                >
                  View Full
                </Button>
                <Space size={8}>
                  <Tooltip title="Download">
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(image);
                      }}
                      className="overlay-btn-small"
                    />
                  </Tooltip>
                  <Tooltip title="Copy URL">
                    <Button
                      icon={<CopyOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyUrl(image.url);
                      }}
                      className="overlay-btn-small"
                    />
                  </Tooltip>
                </Space>
              </Space>
            </div>
          )}
        </div>

        <div className="media-label">
          <Typography.Text ellipsis className="label-text">
            {image.label}
          </Typography.Text>
          {isVideo && <span className="type-badge">Video</span>}
        </div>
      </div>
    );
  };

  const renderPromptCard = (key, prompt) => {
    const isInput = prompt.type === "input";

    return (
      <div key={key} className="prompt-card">
        <div className="prompt-header">
          <span className={`prompt-type ${isInput ? "input" : "output"}`}>
            {isInput ? "📝 Input" : "✨ Output"} Prompt
          </span>
          <Typography.Text className="prompt-label">
            {prompt.label}
          </Typography.Text>
        </div>
        <div className="prompt-content">
          <Typography.Paragraph
            ellipsis={{ rows: 4, expandable: true }}
            className="prompt-text"
          >
            {prompt.content}
          </Typography.Paragraph>
        </div>
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyToClipboard(prompt.content)}
          className="prompt-copy-btn"
        >
          Copy
        </Button>
      </div>
    );
  };

  const hasContent =
    inputs.length > 0 || outputs.length > 0 || Object.keys(prompts).length > 0;

  if (!hasContent) {
    return (
      <div className="media-gallery-empty">
        <Empty description="No media content available" />
      </div>
    );
  }

  return (
    <div className="media-gallery">
      {inputs.length > 0 && (
        <div className="gallery-section">
          <h3 className="section-title">
            📥 Input Images
            <span className="count-badge">{inputs.length}</span>
          </h3>
          <div className="media-grid">
            {inputs.map((image) => renderImageCard(image, true))}
          </div>
        </div>
      )}

      {outputs.length > 0 && (
        <div className="gallery-section">
          <h3 className="section-title">
            🎨 Output Images
            <span className="count-badge">{outputs.length}</span>
            {outputs.length > 1 && (
              <span className="carousel-counter">
                ({currentOutputIndex + 1}/{outputs.length})
              </span>
            )}
          </h3>
          
          {outputs.length > 1 ? (
            // Carousel view for multiple outputs
            <div 
              className="output-carousel"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="carousel-track"
                style={{
                  transform: `translateX(${touchMove - currentOutputIndex * 100}%)`,
                  transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                {outputs.map((image, index) => (
                  <div key={image.id} className="carousel-slide">
                    {renderImageCard(image, false)}
                  </div>
                ))}
              </div>
              
              {/* Navigation arrows */}
              <button
                className={`carousel-arrow carousel-arrow-left ${currentOutputIndex === 0 ? 'disabled' : ''}`}
                onClick={() => currentOutputIndex > 0 && setCurrentOutputIndex(prev => prev - 1)}
                disabled={currentOutputIndex === 0}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                className={`carousel-arrow carousel-arrow-right ${currentOutputIndex === outputs.length - 1 ? 'disabled' : ''}`}
                onClick={() => currentOutputIndex < outputs.length - 1 && setCurrentOutputIndex(prev => prev + 1)}
                disabled={currentOutputIndex === outputs.length - 1}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              
              {/* Dots indicator */}
              <div className="carousel-dots">
                {outputs.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${index === currentOutputIndex ? 'active' : ''}`}
                    onClick={() => setCurrentOutputIndex(index)}
                  />
                ))}
              </div>
            </div>
          ) : (
            // Grid view for single output
            <div className="media-grid">
              {outputs.map((image) => renderImageCard(image, false))}
            </div>
          )}
        </div>
      )}

      {Object.keys(prompts).length > 0 && (
        <div className="gallery-section">
          <h3 className="section-title">
            💬 Prompts
            <span className="count-badge">{Object.keys(prompts).length}</span>
          </h3>
          <div className="prompts-grid">
            {Object.entries(prompts).map(([key, prompt]) =>
              renderPromptCard(key, prompt)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
